import { Plus, Search, Star, Users, MoreHorizontal, Upload, MessageSquare, FileText, Send, Copy, RefreshCw, Bot, Wand2, Calculator, Settings, User, CreditCard, FileSignature, Folder, ChevronDown, ChevronRight, Scale, Briefcase, PenTool, Sparkles, Download, Printer, Share2, Edit, Trash2, Eye, Clock, Check, X, Home, Bell, LayoutTemplate, Palette, Sun, Moon, Monitor, Lock, Rocket } from "lucide-react";
import { cn } from "@/lib/utils";

// Wrapper component for all mockups
const MockupWrapper = ({ children, label }: { children: React.ReactNode; label: string }) => (
  <div className="my-6 rounded-xl border-2 border-dashed border-primary/30 bg-muted/20 p-4">
    <div className="mb-3 flex items-center gap-2 text-sm font-medium text-primary">
      <Eye className="h-4 w-4" />
      <span>Como você verá na plataforma</span>
    </div>
    <div className="rounded-lg border bg-card shadow-sm overflow-hidden">
      {children}
    </div>
    <p className="mt-2 text-center text-xs text-muted-foreground italic">{label}</p>
  </div>
);

// === CASOS ===
export const MockupCasosHeader = () => (
  <MockupWrapper label="Este é o header da página de Casos">
    <div className="p-4 space-y-4">
      <div>
        <h2 className="text-xl font-bold flex items-center gap-2">
          <Briefcase className="h-5 w-5 text-primary" />
          Casos / Clientes
        </h2>
        <p className="text-sm text-muted-foreground">
          Gerencie seus casos jurídicos com documentos e fontes organizadas.
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <button className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 transition-colors">
          <Plus className="h-4 w-4" />
          Novo
        </button>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <div className="pl-9 pr-3 py-2 border rounded-md text-sm text-muted-foreground bg-background w-48">
            Buscar por nome...
          </div>
        </div>
        <div className="flex gap-1 p-1 bg-muted rounded-lg">
          <span className="px-3 py-1.5 bg-background rounded text-sm font-medium shadow-sm">Todos</span>
          <span className="px-3 py-1.5 text-sm text-muted-foreground flex items-center gap-1">
            <Star className="h-3 w-3" /> Destacados
          </span>
          <span className="px-3 py-1.5 text-sm text-muted-foreground flex items-center gap-1">
            <Users className="h-3 w-3" /> Compartilhados
          </span>
        </div>
      </div>
    </div>
  </MockupWrapper>
);

export const MockupCasoCard = () => (
  <MockupWrapper label="Este é um card de caso">
    <div className="p-4 max-w-sm">
      <div className="rounded-lg border bg-card overflow-hidden">
        <div className="h-20 bg-gradient-to-br from-blue-500 to-purple-600 relative">
          <button className="absolute top-2 right-2 p-1.5 rounded-full bg-white/20 hover:bg-white/30">
            <MoreHorizontal className="h-4 w-4 text-white" />
          </button>
        </div>
        <div className="p-4 space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-primary/10">
                <Scale className="h-4 w-4 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-sm">João Silva - Trabalhista</h3>
                <p className="text-xs text-muted-foreground">Reclamação de horas extras</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4 text-xs text-muted-foreground pt-2">
            <span className="flex items-center gap-1">
              <FileText className="h-3 w-3" /> 3 fontes
            </span>
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" /> 2 docs
            </span>
          </div>
        </div>
      </div>
    </div>
  </MockupWrapper>
);

export const MockupFolderCard = () => (
  <MockupWrapper label="Este é um card de pasta">
    <div className="p-4 max-w-sm">
      <div className="rounded-lg border bg-card p-4 flex items-center gap-3">
        <div className="p-2 rounded-lg bg-yellow-100 dark:bg-yellow-900/30">
          <Folder className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-sm">Clientes 2024</h3>
          <p className="text-xs text-muted-foreground">5 casos</p>
        </div>
        <button className="p-1.5 rounded hover:bg-muted">
          <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
        </button>
      </div>
    </div>
  </MockupWrapper>
);

export const MockupBaseConhecimento = () => (
  <MockupWrapper label="Esta é a área de Base de Conhecimento do caso">
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold flex items-center gap-2">
          <FileText className="h-4 w-4 text-primary" />
          Base de Conhecimento
        </h3>
        <button className="flex items-center gap-2 px-3 py-1.5 bg-primary text-primary-foreground rounded-md text-sm">
          <Upload className="h-4 w-4" />
          Upload
        </button>
      </div>
      <div className="border-2 border-dashed border-muted-foreground/30 rounded-lg p-6 text-center">
        <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
        <p className="text-sm text-muted-foreground">Arraste arquivos aqui ou clique para fazer upload</p>
        <p className="text-xs text-muted-foreground mt-1">PDF, DOC, DOCX até 10MB</p>
      </div>
      <div className="space-y-2">
        <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
          <FileText className="h-5 w-5 text-red-500" />
          <div className="flex-1">
            <p className="text-sm font-medium">Contrato_Servicos.pdf</p>
            <p className="text-xs text-muted-foreground">2.3 MB • Processado</p>
          </div>
          <Check className="h-4 w-4 text-green-500" />
        </div>
        <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
          <FileText className="h-5 w-5 text-blue-500" />
          <div className="flex-1">
            <p className="text-sm font-medium">Recurso_Ordinario.docx</p>
            <p className="text-xs text-muted-foreground">1.1 MB • Processado</p>
          </div>
          <Check className="h-4 w-4 text-green-500" />
        </div>
      </div>
    </div>
  </MockupWrapper>
);

export const MockupChatCaso = () => (
  <MockupWrapper label="Este é o chat do caso com a IA">
    <div className="flex flex-col h-80">
      <div className="border-b p-3 flex items-center gap-2">
        <MessageSquare className="h-4 w-4 text-primary" />
        <span className="font-medium text-sm">Chat do Caso</span>
      </div>
      <div className="flex-1 p-4 space-y-4 overflow-auto">
        <div className="flex gap-3">
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <User className="h-4 w-4 text-primary" />
          </div>
          <div className="bg-muted rounded-lg p-3 max-w-[80%]">
            <p className="text-sm">Quais são os principais argumentos para contestar as horas extras neste caso?</p>
          </div>
        </div>
        <div className="flex gap-3">
          <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
            <Sparkles className="h-4 w-4 text-primary-foreground" />
          </div>
          <div className="bg-primary/10 rounded-lg p-3 max-w-[80%]">
            <p className="text-sm">Com base nos documentos do caso, identifiquei os seguintes argumentos principais:</p>
            <ol className="text-sm mt-2 ml-4 list-decimal space-y-1">
              <li>Falta de controle de jornada válido</li>
              <li>Registros de ponto inconsistentes</li>
              <li>Testemunhas contraditórias</li>
            </ol>
          </div>
        </div>
      </div>
      <div className="border-t p-3">
        <div className="flex items-center gap-2">
          <div className="flex-1 flex items-center gap-2 px-3 py-2 border rounded-lg bg-background">
            <input type="text" placeholder="Digite sua pergunta..." className="flex-1 text-sm bg-transparent outline-none" />
            <Upload className="h-4 w-4 text-muted-foreground cursor-pointer" />
          </div>
          <button className="p-2 bg-primary text-primary-foreground rounded-lg">
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  </MockupWrapper>
);

export const MockupDocumentosArea = () => (
  <MockupWrapper label="Esta é a área de criação de documentos">
    <div className="p-4 space-y-4">
      <h3 className="font-semibold flex items-center gap-2">
        <FileText className="h-4 w-4 text-primary" />
        Criar Documento
      </h3>
      <div className="flex gap-3 flex-wrap">
        <button className="flex items-center gap-2 px-4 py-2.5 border-2 border-primary rounded-lg text-sm font-medium hover:bg-primary/5 transition-colors">
          <FileText className="h-4 w-4 text-primary" />
          Doc em Branco
        </button>
        <button className="flex items-center gap-2 px-4 py-2.5 border rounded-lg text-sm hover:bg-muted transition-colors">
          <PenTool className="h-4 w-4" />
          Petição Inicial
        </button>
        <button className="flex items-center gap-2 px-4 py-2.5 border rounded-lg text-sm hover:bg-muted transition-colors">
          <Scale className="h-4 w-4" />
          Contestação
        </button>
      </div>
      <div className="border rounded-lg p-3">
        <p className="text-sm text-muted-foreground mb-2">Descreva o documento que precisa:</p>
        <textarea 
          className="w-full h-20 text-sm bg-muted/30 rounded-lg p-2 resize-none" 
          placeholder="Ex: Petição inicial trabalhista para reclamação de horas extras não pagas nos últimos 2 anos..."
        />
        <div className="flex justify-end mt-2">
          <button className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm">
            <Sparkles className="h-4 w-4" />
            Gerar Documento
          </button>
        </div>
      </div>
    </div>
  </MockupWrapper>
);

export const MockupHistoricoDocs = () => (
  <MockupWrapper label="Este é o histórico de documentos do caso">
    <div className="p-4 space-y-3">
      <h3 className="font-semibold flex items-center gap-2">
        <Clock className="h-4 w-4 text-primary" />
        Histórico de Documentos
      </h3>
      {[
        { title: "Petição Inicial Trabalhista", date: "15/01/2024", type: "Petição" },
        { title: "Contestação - Horas Extras", date: "10/01/2024", type: "Contestação" },
        { title: "Procuração Ad Judicia", date: "05/01/2024", type: "Procuração" },
      ].map((doc, i) => (
        <div key={i} className="flex items-center gap-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors cursor-pointer">
          <FileText className="h-5 w-5 text-primary" />
          <div className="flex-1">
            <p className="text-sm font-medium">{doc.title}</p>
            <p className="text-xs text-muted-foreground">{doc.date} • {doc.type}</p>
          </div>
          <div className="flex items-center gap-1">
            <button className="p-1.5 rounded hover:bg-muted">
              <Eye className="h-4 w-4 text-muted-foreground" />
            </button>
            <button className="p-1.5 rounded hover:bg-muted">
              <Download className="h-4 w-4 text-muted-foreground" />
            </button>
            <button className="p-1.5 rounded hover:bg-muted">
              <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
            </button>
          </div>
        </div>
      ))}
    </div>
  </MockupWrapper>
);

export const MockupCompartilhar = () => (
  <MockupWrapper label="Este é o diálogo de compartilhamento">
    <div className="p-4 max-w-md">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold flex items-center gap-2">
          <Share2 className="h-4 w-4 text-primary" />
          Compartilhar Caso
        </h3>
        <button className="p-1 rounded hover:bg-muted">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="space-y-4">
        <div>
          <label className="text-sm font-medium mb-1.5 block">Adicionar pessoas</label>
          <div className="flex gap-2">
            <input type="email" placeholder="email@exemplo.com" className="flex-1 px-3 py-2 border rounded-lg text-sm" />
            <button className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm">
              Adicionar
            </button>
          </div>
        </div>
        <div>
          <label className="text-sm font-medium mb-1.5 block">Permissão</label>
          <div className="flex gap-2">
            <button className="flex-1 px-3 py-2 bg-muted rounded-lg text-sm font-medium">Visualizar</button>
            <button className="flex-1 px-3 py-2 border rounded-lg text-sm">Editar</button>
            <button className="flex-1 px-3 py-2 border rounded-lg text-sm">Completo</button>
          </div>
        </div>
        <div className="pt-2 border-t">
          <p className="text-sm font-medium mb-2">Compartilhado com:</p>
          <div className="flex items-center gap-3 p-2 rounded-lg bg-muted/50">
            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
              <User className="h-4 w-4 text-primary" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium">Maria Santos</p>
              <p className="text-xs text-muted-foreground">maria@escritorio.com</p>
            </div>
            <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded">Editar</span>
          </div>
        </div>
      </div>
    </div>
  </MockupWrapper>
);

// === CHAT JURÍDICO ===
export const MockupChatJuridico = () => (
  <MockupWrapper label="Esta é a interface do Chat Jurídico">
    <div className="flex h-96">
      <div className="w-48 border-r p-3 space-y-2 bg-muted/30">
        <button className="w-full flex items-center gap-2 px-3 py-2 bg-primary text-primary-foreground rounded-lg text-sm">
          <Plus className="h-4 w-4" />
          Nova Conversa
        </button>
        <div className="space-y-1 pt-2">
          <p className="text-xs text-muted-foreground px-2">Recentes</p>
          <button className="w-full text-left px-3 py-2 rounded-lg bg-muted text-sm truncate">
            Dúvida sobre FGTS
          </button>
          <button className="w-full text-left px-3 py-2 rounded-lg hover:bg-muted text-sm text-muted-foreground truncate">
            Prazo recursal
          </button>
          <button className="w-full text-left px-3 py-2 rounded-lg hover:bg-muted text-sm text-muted-foreground truncate">
            Súmula 331 TST
          </button>
        </div>
      </div>
      <div className="flex-1 flex flex-col">
        <div className="flex-1 p-4 space-y-4 overflow-auto">
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <User className="h-4 w-4 text-primary" />
            </div>
            <div className="bg-muted rounded-lg p-3 max-w-[80%]">
              <p className="text-sm">Qual o prazo para depósito do FGTS após demissão sem justa causa?</p>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
              <Sparkles className="h-4 w-4 text-primary-foreground" />
            </div>
            <div className="bg-primary/10 rounded-lg p-3 max-w-[80%]">
              <p className="text-sm">O empregador deve depositar o FGTS até o <strong>primeiro dia útil</strong> subsequente à data do efetivo desligamento.</p>
              <p className="text-sm mt-2 text-muted-foreground">Fundamento: Art. 18 da Lei 8.036/90</p>
              <div className="flex gap-2 mt-3">
                <button className="p-1.5 rounded hover:bg-muted">
                  <Copy className="h-4 w-4 text-muted-foreground" />
                </button>
                <button className="p-1.5 rounded hover:bg-muted">
                  <RefreshCw className="h-4 w-4 text-muted-foreground" />
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="border-t p-3">
          <div className="flex items-center gap-2">
            <div className="flex-1 flex items-center gap-2 px-3 py-2 border rounded-lg bg-background">
              <input type="text" placeholder="Digite sua dúvida jurídica..." className="flex-1 text-sm bg-transparent outline-none" />
              <Upload className="h-4 w-4 text-muted-foreground cursor-pointer" />
            </div>
            <button className="p-2 bg-primary text-primary-foreground rounded-lg">
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </MockupWrapper>
);

export const MockupFerramentasChat = () => (
  <MockupWrapper label="Estas são as ferramentas disponíveis nas mensagens">
    <div className="p-4">
      <div className="bg-primary/10 rounded-lg p-4 max-w-md">
        <p className="text-sm mb-3">O prazo prescricional para ações trabalhistas é de 5 anos durante o contrato de trabalho...</p>
        <div className="flex items-center gap-1 pt-2 border-t border-primary/20">
          <button className="flex items-center gap-1.5 px-3 py-1.5 rounded hover:bg-primary/10 text-sm">
            <Copy className="h-4 w-4" />
            Copiar
          </button>
          <button className="flex items-center gap-1.5 px-3 py-1.5 rounded hover:bg-primary/10 text-sm">
            <RefreshCw className="h-4 w-4" />
            Regenerar
          </button>
          <button className="flex items-center gap-1.5 px-3 py-1.5 rounded hover:bg-primary/10 text-sm">
            <FileText className="h-4 w-4" />
            Salvar
          </button>
        </div>
      </div>
    </div>
  </MockupWrapper>
);

// === ASSESSOR ===
export const MockupAssessor = () => (
  <MockupWrapper label="Esta é a interface do Assessor">
    <div className="flex flex-col h-80">
      <div className="border-b p-3 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center">
          <Bot className="h-5 w-5 text-white" />
        </div>
        <div>
          <h3 className="font-semibold text-sm">Assessor</h3>
          <p className="text-xs text-muted-foreground">Seu assistente personalizado</p>
        </div>
      </div>
      <div className="flex-1 p-4 space-y-4 overflow-auto">
        <div className="flex gap-3">
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <User className="h-4 w-4 text-primary" />
          </div>
          <div className="bg-muted rounded-lg p-3 max-w-[80%]">
            <p className="text-sm">Quais casos tenho com audiência esta semana?</p>
          </div>
        </div>
        <div className="flex gap-3">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center flex-shrink-0">
            <Bot className="h-4 w-4 text-white" />
          </div>
          <div className="bg-primary/10 rounded-lg p-3 max-w-[80%]">
            <p className="text-sm">Encontrei 2 casos com audiência esta semana:</p>
            <ul className="text-sm mt-2 ml-4 list-disc space-y-1">
              <li><strong>João Silva</strong> - Quarta, 10h - Vara do Trabalho</li>
              <li><strong>Maria Santos</strong> - Sexta, 14h - Fórum Cível</li>
            </ul>
          </div>
        </div>
      </div>
      <div className="border-t p-3">
        <div className="flex items-center gap-2">
          <input type="text" placeholder="Pergunte sobre seus casos..." className="flex-1 px-3 py-2 border rounded-lg text-sm" />
          <button className="p-2 bg-primary text-primary-foreground rounded-lg">
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  </MockupWrapper>
);

export const MockupAssessorConfig = () => (
  <MockupWrapper label="Esta é a página de configuração do Assessor">
    <div className="p-4 space-y-4 max-w-lg">
      <h3 className="font-semibold flex items-center gap-2">
        <Settings className="h-4 w-4 text-primary" />
        Configurar Assessor
      </h3>
      <div className="space-y-4">
        <div className="flex items-center justify-between p-3 rounded-lg border">
          <div className="flex items-center gap-3">
            <Bot className="h-5 w-5 text-primary" />
            <div>
              <p className="text-sm font-medium">Assessor ativo</p>
              <p className="text-xs text-muted-foreground">Ativar ou desativar o assistente</p>
            </div>
          </div>
          <div className="w-11 h-6 bg-primary rounded-full relative">
            <div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full" />
          </div>
        </div>
        <div>
          <label className="text-sm font-medium mb-1.5 block">Nome do Assessor</label>
          <input type="text" value="Laib" className="w-full px-3 py-2 border rounded-lg text-sm" />
        </div>
        <div>
          <label className="text-sm font-medium mb-1.5 block">Tom de resposta</label>
          <div className="flex gap-2">
            <button className="px-3 py-2 bg-primary text-primary-foreground rounded-lg text-sm">Profissional</button>
            <button className="px-3 py-2 border rounded-lg text-sm">Amigável</button>
            <button className="px-3 py-2 border rounded-lg text-sm">Formal</button>
          </div>
        </div>
        <div>
          <label className="text-sm font-medium mb-1.5 block">Escopo de acesso</label>
          <div className="flex gap-2">
            <button className="flex-1 px-3 py-2 bg-muted rounded-lg text-sm font-medium">Todos os casos</button>
            <button className="flex-1 px-3 py-2 border rounded-lg text-sm">Selecionar casos</button>
          </div>
        </div>
      </div>
    </div>
  </MockupWrapper>
);

// === MELHORE SEU TEXTO ===
export const MockupMelhoreTexto = () => (
  <MockupWrapper label="Esta é a interface do Melhore seu Texto">
    <div className="p-4 space-y-4">
      <h3 className="font-semibold flex items-center gap-2">
        <Wand2 className="h-4 w-4 text-primary" />
        Melhore seu Texto
      </h3>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium mb-1.5 block">Texto original</label>
          <textarea 
            className="w-full h-32 text-sm border rounded-lg p-3 resize-none" 
            defaultValue="O reclamante trabalhou para a reclamada durante 3 anos, fazendo horas extras todos os dias sem receber pagamento..."
          />
        </div>
        <div>
          <label className="text-sm font-medium mb-1.5 block">Texto melhorado</label>
          <div className="w-full h-32 text-sm border rounded-lg p-3 bg-green-50 dark:bg-green-900/20">
            <p className="text-sm">O Reclamante manteve vínculo empregatício com a Reclamada pelo período de 3 (três) anos, durante o qual <strong>habitualmente realizou jornada extraordinária</strong>, sem a devida contraprestação pecuniária...</p>
          </div>
        </div>
      </div>
      <div className="flex justify-center">
        <button className="flex items-center gap-2 px-6 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium">
          <Sparkles className="h-4 w-4" />
          Melhorar Texto
        </button>
      </div>
    </div>
  </MockupWrapper>
);

// === CALCULADORAS ===
export const MockupCalculadoras = () => (
  <MockupWrapper label="Esta é a área de calculadoras jurídicas">
    <div className="p-4 space-y-4">
      <h3 className="font-semibold flex items-center gap-2">
        <Calculator className="h-4 w-4 text-primary" />
        Calculadoras Jurídicas
      </h3>
      <div className="grid grid-cols-3 gap-3">
        {[
          { icon: Briefcase, name: "Trabalhista", desc: "Rescisão, FGTS, férias" },
          { icon: CreditCard, name: "INSS", desc: "Contribuições e benefícios" },
          { icon: Users, name: "Pensão", desc: "Alimentícia" },
          { icon: Home, name: "Aluguel", desc: "Reajustes e índices" },
          { icon: Scale, name: "Atualização", desc: "Valores monetários" },
          { icon: Calculator, name: "Honorários", desc: "Cálculo de honorários" },
        ].map((calc, i) => (
          <button key={i} className="p-3 border rounded-lg hover:bg-muted/50 transition-colors text-left">
            <calc.icon className="h-5 w-5 text-primary mb-2" />
            <p className="text-sm font-medium">{calc.name}</p>
            <p className="text-xs text-muted-foreground">{calc.desc}</p>
          </button>
        ))}
      </div>
    </div>
  </MockupWrapper>
);

// === CONFIGURAÇÕES ===
export const MockupConfiguracoes = () => (
  <MockupWrapper label="Esta é a página de configurações">
    <div className="flex h-64">
      <div className="w-48 border-r p-3 space-y-1 bg-muted/30">
        {[
          { icon: User, name: "Perfil", active: true },
          { icon: FileSignature, name: "Papel Timbrado" },
          { icon: CreditCard, name: "Plano e Créditos" },
          { icon: Bell, name: "Notificações" },
          { icon: Bot, name: "Assessor" },
        ].map((item, i) => (
          <button key={i} className={cn(
            "w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm",
            item.active ? "bg-primary/10 text-primary font-medium" : "hover:bg-muted text-muted-foreground"
          )}>
            <item.icon className="h-4 w-4" />
            {item.name}
          </button>
        ))}
      </div>
      <div className="flex-1 p-4">
        <h3 className="font-semibold mb-4">Perfil</h3>
        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium mb-1 block">Nome completo</label>
            <input type="text" value="Dr. João Silva" className="w-full px-3 py-2 border rounded-lg text-sm" />
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">OAB</label>
            <input type="text" value="SP 123.456" className="w-full px-3 py-2 border rounded-lg text-sm" />
          </div>
          <button className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm">
            Salvar alterações
          </button>
        </div>
      </div>
    </div>
  </MockupWrapper>
);

export const MockupPapelTimbrado = () => (
  <MockupWrapper label="Esta é a área de configuração do papel timbrado">
    <div className="p-4 space-y-4">
      <h3 className="font-semibold flex items-center gap-2">
        <FileSignature className="h-4 w-4 text-primary" />
        Papel Timbrado
      </h3>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium mb-1.5 block">Logo do escritório</label>
            <div className="border-2 border-dashed rounded-lg p-4 text-center">
              <Upload className="h-6 w-6 mx-auto text-muted-foreground mb-1" />
              <p className="text-xs text-muted-foreground">Clique para fazer upload</p>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium mb-1.5 block">Nome do escritório</label>
            <input type="text" placeholder="Silva & Associados" className="w-full px-3 py-2 border rounded-lg text-sm" />
          </div>
          <div>
            <label className="text-sm font-medium mb-1.5 block">Endereço</label>
            <textarea className="w-full h-16 px-3 py-2 border rounded-lg text-sm resize-none" placeholder="Rua das Flores, 123..." />
          </div>
        </div>
        <div className="border rounded-lg p-4 bg-white">
          <div className="text-center border-b pb-3 mb-3">
            <div className="w-12 h-12 mx-auto bg-muted rounded mb-2" />
            <p className="text-sm font-bold">SILVA & ASSOCIADOS</p>
            <p className="text-xs text-muted-foreground">Advocacia Especializada</p>
          </div>
          <div className="space-y-2">
            <div className="h-2 bg-muted rounded w-full" />
            <div className="h-2 bg-muted rounded w-4/5" />
            <div className="h-2 bg-muted rounded w-full" />
            <div className="h-2 bg-muted rounded w-3/4" />
          </div>
        </div>
      </div>
    </div>
  </MockupWrapper>
);

export const MockupPlanosCreditos = () => (
  <MockupWrapper label="Esta é a área de planos e créditos">
    <div className="p-4 space-y-4">
      <h3 className="font-semibold flex items-center gap-2">
        <CreditCard className="h-4 w-4 text-primary" />
        Plano e Créditos
      </h3>
      <div className="p-4 rounded-lg border bg-gradient-to-r from-primary/10 to-purple-500/10">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-sm font-medium">Plano Profissional</p>
            <p className="text-xs text-muted-foreground">Renovação em 15/02/2024</p>
          </div>
          <button className="px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-sm">
            Upgrade
          </button>
        </div>
        <div className="space-y-2">
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span>Créditos de IA</span>
              <span>750 / 1000</span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-primary rounded-full" style={{ width: '75%' }} />
            </div>
          </div>
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span>Armazenamento</span>
              <span>2.1 GB / 5 GB</span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-green-500 rounded-full" style={{ width: '42%' }} />
            </div>
          </div>
        </div>
      </div>
      <div className="flex gap-2">
        <button className="flex-1 px-4 py-2 border rounded-lg text-sm hover:bg-muted">
          Comprar créditos
        </button>
        <button className="flex-1 px-4 py-2 border rounded-lg text-sm hover:bg-muted">
          Ver histórico
        </button>
      </div>
    </div>
  </MockupWrapper>
);

// === SIDEBAR ===
export const MockupSidebar = () => (
  <MockupWrapper label="Esta é a sidebar de navegação">
    <div className="w-56 p-3 space-y-1 bg-muted/30">
      {[
        { icon: Home, name: "Início", active: false },
        { icon: Briefcase, name: "Casos / Clientes", active: true },
        { icon: MessageSquare, name: "Chat Jurídico" },
        { icon: Bot, name: "Assessor" },
        { icon: Wand2, name: "Melhore seu Texto" },
        { icon: Calculator, name: "Cálculos Jurídicos" },
        { icon: Settings, name: "Configurações" },
      ].map((item, i) => (
        <button key={i} className={cn(
          "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors",
          item.active 
            ? "bg-primary text-primary-foreground font-medium" 
            : "hover:bg-muted text-muted-foreground hover:text-foreground"
        )}>
          <item.icon className="h-4 w-4" />
          {item.name}
        </button>
      ))}
    </div>
  </MockupWrapper>
);

// === WELCOME / INTERFACE ===
export const MockupWelcome = () => (
  <MockupWrapper label="Esta é a tela de boas-vindas do Justiçai">
    <div className="p-6 space-y-6">
      <div className="text-center space-y-2">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center">
          <Scale className="h-8 w-8 text-white" />
        </div>
        <h2 className="text-xl font-bold">Bem-vindo ao Justiçai</h2>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">
          Sua plataforma de IA 100% focada no Direito Brasileiro
        </p>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {[
          { icon: Briefcase, name: "Casos", desc: "Gerencie processos" },
          { icon: MessageSquare, name: "Chat IA", desc: "Tire dúvidas" },
          { icon: FileText, name: "Documentos", desc: "Crie peças" },
        ].map((item, i) => (
          <div key={i} className="p-4 border rounded-lg text-center hover:bg-muted/50 transition-colors cursor-pointer">
            <item.icon className="h-6 w-6 mx-auto text-primary mb-2" />
            <p className="text-sm font-medium">{item.name}</p>
            <p className="text-xs text-muted-foreground">{item.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </MockupWrapper>
);

export const MockupInterfaceOverview = () => (
  <MockupWrapper label="Visão geral da interface do Justiçai">
    <div className="flex h-64">
      <div className="w-44 border-r p-2 bg-muted/30 space-y-1">
        <div className="flex items-center gap-2 p-2 text-xs font-medium text-muted-foreground">
          <div className="w-6 h-6 rounded bg-primary/20" />
          <span>Justiçai</span>
        </div>
        <div className="flex items-center gap-2 p-2 text-xs rounded bg-primary text-primary-foreground">
          <Briefcase className="h-3 w-3" />
          <span>Casos</span>
        </div>
        <div className="flex items-center gap-2 p-2 text-xs text-muted-foreground">
          <MessageSquare className="h-3 w-3" />
          <span>Chat Jurídico</span>
        </div>
        <div className="flex items-center gap-2 p-2 text-xs text-muted-foreground">
          <Bot className="h-3 w-3" />
          <span>Assessor</span>
        </div>
        <div className="absolute left-[4.5rem] top-12 text-xs bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 px-2 py-1 rounded shadow-sm border border-yellow-300 dark:border-yellow-700">
          ← Sidebar
        </div>
      </div>
      <div className="flex-1 flex flex-col">
        <div className="h-10 border-b flex items-center justify-between px-3 bg-muted/20">
          <div className="flex items-center gap-2 px-3 py-1 border rounded text-xs text-muted-foreground">
            <Search className="h-3 w-3" />
            <span>Buscar...</span>
          </div>
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4 text-muted-foreground" />
            <div className="w-6 h-6 rounded-full bg-primary/20" />
          </div>
        </div>
        <div className="flex-1 p-4 relative">
          <div className="text-lg font-bold mb-2">Meus Casos</div>
          <div className="grid grid-cols-2 gap-2">
            <div className="p-3 border rounded bg-card h-16" />
            <div className="p-3 border rounded bg-card h-16" />
          </div>
          <div className="absolute top-2 right-2 text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded shadow-sm border border-blue-300 dark:border-blue-700">
            Área Principal →
          </div>
        </div>
      </div>
    </div>
  </MockupWrapper>
);

// === CHAT - ANEXOS E HISTÓRICO ===
export const MockupAnexos = () => (
  <MockupWrapper label="Área de enviar anexos no chat">
    <div className="p-4 space-y-4">
      <div className="flex items-center gap-2 px-3 py-2 border rounded-lg">
        <input type="text" placeholder="Digite sua mensagem..." className="flex-1 text-sm bg-transparent outline-none" />
        <button className="p-2 rounded-lg hover:bg-muted relative group">
          <Upload className="h-4 w-4 text-muted-foreground" />
          <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-foreground text-background text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap">
            Anexar arquivo
          </div>
        </button>
        <button className="p-2 bg-primary text-primary-foreground rounded-lg">
          <Send className="h-4 w-4" />
        </button>
      </div>
      <div className="border-2 border-dashed border-primary/40 rounded-lg p-4 bg-primary/5">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <Upload className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium">Arraste arquivos aqui</p>
            <p className="text-xs text-muted-foreground">ou clique no ícone de anexo</p>
          </div>
        </div>
      </div>
      <div className="space-y-2">
        <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/50">
          <FileText className="h-4 w-4 text-red-500" />
          <span className="flex-1 text-sm truncate">Documento_anexado.pdf</span>
          <Check className="h-4 w-4 text-green-500" />
          <button className="p-1 hover:bg-muted rounded">
            <X className="h-3 w-3 text-muted-foreground" />
          </button>
        </div>
      </div>
    </div>
  </MockupWrapper>
);

export const MockupHistoricoChat = () => (
  <MockupWrapper label="Lista de conversas anteriores">
    <div className="w-64 p-3 space-y-2 bg-muted/30">
      <button className="w-full flex items-center gap-2 px-3 py-2 bg-primary text-primary-foreground rounded-lg text-sm">
        <Plus className="h-4 w-4" />
        Nova Conversa
      </button>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
        <input type="text" placeholder="Buscar conversas..." className="w-full pl-8 pr-3 py-1.5 text-xs border rounded-lg bg-background" />
      </div>
      <div className="space-y-1 pt-2">
        <p className="text-xs text-muted-foreground px-2 font-medium">Hoje</p>
        <button className="w-full text-left px-3 py-2 rounded-lg bg-muted text-sm truncate flex items-center gap-2">
          <MessageSquare className="h-3 w-3 flex-shrink-0" />
          <span className="truncate">Dúvida sobre FGTS e multa</span>
        </button>
        <button className="w-full text-left px-3 py-2 rounded-lg hover:bg-muted text-sm text-muted-foreground truncate flex items-center gap-2">
          <MessageSquare className="h-3 w-3 flex-shrink-0" />
          <span className="truncate">Prazo recursal CLT</span>
        </button>
        <p className="text-xs text-muted-foreground px-2 font-medium pt-2">Ontem</p>
        <button className="w-full text-left px-3 py-2 rounded-lg hover:bg-muted text-sm text-muted-foreground truncate flex items-center gap-2">
          <MessageSquare className="h-3 w-3 flex-shrink-0" />
          <span className="truncate">Súmula 331 TST análise</span>
        </button>
        <button className="w-full text-left px-3 py-2 rounded-lg hover:bg-muted text-sm text-muted-foreground truncate flex items-center gap-2">
          <MessageSquare className="h-3 w-3 flex-shrink-0" />
          <span className="truncate">Cálculo de horas extras</span>
        </button>
      </div>
    </div>
  </MockupWrapper>
);

// === ASSESSOR - ESCOPO E TOM ===
export const MockupEscopoAssessor = () => (
  <MockupWrapper label="Configuração de escopo do Assessor">
    <div className="p-4 space-y-4 max-w-md">
      <h3 className="font-semibold flex items-center gap-2">
        <Eye className="h-4 w-4 text-primary" />
        Escopo de Acesso
      </h3>
      <p className="text-sm text-muted-foreground">Defina quais casos o Assessor pode acessar para responder suas perguntas.</p>
      <div className="space-y-2">
        <button className="w-full flex items-center gap-3 p-3 rounded-lg border-2 border-primary bg-primary/5">
          <div className="w-4 h-4 rounded-full border-4 border-primary" />
          <div className="flex-1 text-left">
            <p className="text-sm font-medium">Todos os casos</p>
            <p className="text-xs text-muted-foreground">Acesso completo a todos os seus casos</p>
          </div>
        </button>
        <button className="w-full flex items-center gap-3 p-3 rounded-lg border hover:bg-muted/50">
          <div className="w-4 h-4 rounded-full border-2 border-muted-foreground" />
          <div className="flex-1 text-left">
            <p className="text-sm font-medium">Casos selecionados</p>
            <p className="text-xs text-muted-foreground">Escolha manualmente quais casos</p>
          </div>
        </button>
        <button className="w-full flex items-center gap-3 p-3 rounded-lg border hover:bg-muted/50">
          <div className="w-4 h-4 rounded-full border-2 border-muted-foreground" />
          <div className="flex-1 text-left">
            <p className="text-sm font-medium">Nenhum caso</p>
            <p className="text-xs text-muted-foreground">Apenas conhecimento jurídico geral</p>
          </div>
        </button>
      </div>
    </div>
  </MockupWrapper>
);

export const MockupTomResposta = () => (
  <MockupWrapper label="Configuração do tom de resposta">
    <div className="p-4 space-y-4 max-w-md">
      <h3 className="font-semibold flex items-center gap-2">
        <MessageSquare className="h-4 w-4 text-primary" />
        Tom de Resposta
      </h3>
      <p className="text-sm text-muted-foreground">Escolha como o Assessor deve se comunicar com você.</p>
      <div className="grid grid-cols-2 gap-2">
        <button className="p-3 rounded-lg border-2 border-primary bg-primary/5 text-center">
          <div className="text-lg mb-1">👔</div>
          <p className="text-sm font-medium">Profissional</p>
          <p className="text-xs text-muted-foreground">Equilibrado e claro</p>
        </button>
        <button className="p-3 rounded-lg border hover:bg-muted/50 text-center">
          <div className="text-lg mb-1">😊</div>
          <p className="text-sm font-medium">Amigável</p>
          <p className="text-xs text-muted-foreground">Leve e acessível</p>
        </button>
        <button className="p-3 rounded-lg border hover:bg-muted/50 text-center">
          <div className="text-lg mb-1">📜</div>
          <p className="text-sm font-medium">Formal</p>
          <p className="text-xs text-muted-foreground">Técnico e rebuscado</p>
        </button>
        <button className="p-3 rounded-lg border hover:bg-muted/50 text-center">
          <div className="text-lg mb-1">⚡</div>
          <p className="text-sm font-medium">Direto</p>
          <p className="text-xs text-muted-foreground">Respostas curtas</p>
        </button>
      </div>
    </div>
  </MockupWrapper>
);

// === MELHORE SEU TEXTO - TIPOS ===
export const MockupTiposMelhoria = () => (
  <MockupWrapper label="Tipos de melhoria disponíveis">
    <div className="p-4 space-y-4">
      <h3 className="font-semibold flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-primary" />
        Escolha o Tipo de Melhoria
      </h3>
      <div className="grid grid-cols-3 gap-2">
        {[
          { icon: Check, name: "Gramática", desc: "Corrige erros", color: "text-green-500" },
          { icon: Scale, name: "Argumentação", desc: "Fortalece teses", color: "text-blue-500" },
          { icon: Sparkles, name: "Simplificar", desc: "Mais claro", color: "text-yellow-500" },
          { icon: FileSignature, name: "Formalizar", desc: "Mais técnico", color: "text-purple-500" },
          { icon: FileText, name: "Resumir", desc: "Versão curta", color: "text-orange-500" },
          { icon: PenTool, name: "Expandir", desc: "Mais detalhes", color: "text-pink-500" },
        ].map((tipo, i) => (
          <button key={i} className="p-3 border rounded-lg hover:bg-muted/50 transition-colors text-left">
            <tipo.icon className={cn("h-5 w-5 mb-2", tipo.color)} />
            <p className="text-sm font-medium">{tipo.name}</p>
            <p className="text-xs text-muted-foreground">{tipo.desc}</p>
          </button>
        ))}
      </div>
    </div>
  </MockupWrapper>
);

// === CALCULADORAS ESPECÍFICAS ===
export const MockupCalcTrabalhista = () => (
  <MockupWrapper label="Calculadora de verbas trabalhistas">
    <div className="p-4 space-y-4 max-w-md">
      <h3 className="font-semibold flex items-center gap-2">
        <Briefcase className="h-4 w-4 text-primary" />
        Cálculos Trabalhistas
      </h3>
      <div className="space-y-3">
        <div>
          <label className="text-sm font-medium mb-1 block">Salário mensal (R$)</label>
          <input type="text" value="3.500,00" className="w-full px-3 py-2 border rounded-lg text-sm" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-medium mb-1 block">Dias trabalhados</label>
            <input type="text" value="15" className="w-full px-3 py-2 border rounded-lg text-sm" />
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">Meses trabalhados</label>
            <input type="text" value="24" className="w-full px-3 py-2 border rounded-lg text-sm" />
          </div>
        </div>
        <div className="flex gap-3">
          <label className="flex items-center gap-2 text-sm">
            <div className="w-4 h-4 rounded border-2 border-primary bg-primary flex items-center justify-center">
              <Check className="h-3 w-3 text-primary-foreground" />
            </div>
            Férias vencidas
          </label>
          <label className="flex items-center gap-2 text-sm">
            <div className="w-4 h-4 rounded border-2 border-primary bg-primary flex items-center justify-center">
              <Check className="h-3 w-3 text-primary-foreground" />
            </div>
            13º proporcional
          </label>
        </div>
        <button className="w-full py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium">
          Calcular Verbas
        </button>
        <div className="p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
          <p className="text-sm font-medium text-green-800 dark:text-green-200">Total: R$ 12.847,50</p>
        </div>
      </div>
    </div>
  </MockupWrapper>
);

export const MockupCalcINSS = () => (
  <MockupWrapper label="Calculadora INSS">
    <div className="p-4 space-y-4 max-w-md">
      <h3 className="font-semibold flex items-center gap-2">
        <CreditCard className="h-4 w-4 text-primary" />
        Cálculo INSS
      </h3>
      <div className="space-y-3">
        <div>
          <label className="text-sm font-medium mb-1 block">Valor pago indevidamente (R$)</label>
          <input type="text" value="5.000,00" className="w-full px-3 py-2 border rounded-lg text-sm" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-medium mb-1 block">Data inicial</label>
            <input type="text" value="01/01/2020" className="w-full px-3 py-2 border rounded-lg text-sm" />
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">Data final</label>
            <input type="text" value="01/01/2024" className="w-full px-3 py-2 border rounded-lg text-sm" />
          </div>
        </div>
        <button className="w-full py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium">
          Calcular Restituição
        </button>
        <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 space-y-1">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Valor original:</span>
            <span>R$ 5.000,00</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Correção SELIC:</span>
            <span>R$ 1.847,32</span>
          </div>
          <div className="flex justify-between text-sm font-medium text-blue-800 dark:text-blue-200">
            <span>Total a restituir:</span>
            <span>R$ 6.847,32</span>
          </div>
        </div>
      </div>
    </div>
  </MockupWrapper>
);

export const MockupCalcAtualizacao = () => (
  <MockupWrapper label="Calculadora de atualização monetária">
    <div className="p-4 space-y-4 max-w-md">
      <h3 className="font-semibold flex items-center gap-2">
        <Calculator className="h-4 w-4 text-primary" />
        Atualização de Valores
      </h3>
      <div className="space-y-3">
        <div>
          <label className="text-sm font-medium mb-1 block">Valor original (R$)</label>
          <input type="text" value="10.000,00" className="w-full px-3 py-2 border rounded-lg text-sm" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-medium mb-1 block">Data inicial</label>
            <input type="text" value="01/01/2019" className="w-full px-3 py-2 border rounded-lg text-sm" />
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">Data final</label>
            <input type="text" value="01/01/2024" className="w-full px-3 py-2 border rounded-lg text-sm" />
          </div>
        </div>
        <div>
          <label className="text-sm font-medium mb-1.5 block">Índice de correção</label>
          <div className="flex gap-2">
            <button className="px-3 py-2 bg-primary text-primary-foreground rounded-lg text-sm">IPCA</button>
            <button className="px-3 py-2 border rounded-lg text-sm">INPC</button>
            <button className="px-3 py-2 border rounded-lg text-sm">SELIC</button>
            <button className="px-3 py-2 border rounded-lg text-sm">TR</button>
          </div>
        </div>
        <button className="w-full py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium">
          Calcular Atualização
        </button>
        <div className="p-3 rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800">
          <div className="flex justify-between text-sm font-medium text-purple-800 dark:text-purple-200">
            <span>Valor atualizado:</span>
            <span>R$ 13.542,18</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">Correção de 35,42% pelo IPCA</p>
        </div>
      </div>
    </div>
  </MockupWrapper>
);

export const MockupCalcPensao = () => (
  <MockupWrapper label="Calculadora de pensão alimentícia">
    <div className="p-4 space-y-4 max-w-md">
      <h3 className="font-semibold flex items-center gap-2">
        <Users className="h-4 w-4 text-primary" />
        Pensão Alimentícia
      </h3>
      <div className="space-y-3">
        <div>
          <label className="text-sm font-medium mb-1 block">Salário líquido do alimentante (R$)</label>
          <input type="text" value="8.000,00" className="w-full px-3 py-2 border rounded-lg text-sm" />
        </div>
        <div>
          <label className="text-sm font-medium mb-1 block">Percentual (%)</label>
          <input type="text" value="30" className="w-full px-3 py-2 border rounded-lg text-sm" />
        </div>
        <button className="w-full py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium">
          Calcular Pensão
        </button>
        <div className="p-4 rounded-lg bg-gradient-to-r from-primary/10 to-purple-500/10 border">
          <div className="text-center">
            <p className="text-sm text-muted-foreground">Valor da pensão mensal</p>
            <p className="text-2xl font-bold text-primary">R$ 2.400,00</p>
            <p className="text-xs text-muted-foreground mt-1">30% de R$ 8.000,00</p>
          </div>
        </div>
      </div>
    </div>
  </MockupWrapper>
);

export const MockupCalcAluguel = () => (
  <MockupWrapper label="Calculadora de aluguel atrasado">
    <div className="p-4 space-y-4 max-w-md">
      <h3 className="font-semibold flex items-center gap-2">
        <Home className="h-4 w-4 text-primary" />
        Cálculo de Aluguel
      </h3>
      <div className="space-y-3">
        <div>
          <label className="text-sm font-medium mb-1 block">Valor do aluguel (R$)</label>
          <input type="text" value="2.500,00" className="w-full px-3 py-2 border rounded-lg text-sm" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-medium mb-1 block">Dias em atraso</label>
            <input type="text" value="45" className="w-full px-3 py-2 border rounded-lg text-sm" />
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">Multa (%)</label>
            <input type="text" value="10" className="w-full px-3 py-2 border rounded-lg text-sm" />
          </div>
        </div>
        <div>
          <label className="text-sm font-medium mb-1 block">Juros diários (%)</label>
          <input type="text" value="0,33" className="w-full px-3 py-2 border rounded-lg text-sm" />
        </div>
        <button className="w-full py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium">
          Calcular Total
        </button>
        <div className="p-3 rounded-lg bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 space-y-1">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Aluguel:</span>
            <span>R$ 2.500,00</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Multa (10%):</span>
            <span>R$ 250,00</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Juros (45 dias):</span>
            <span>R$ 371,25</span>
          </div>
          <div className="flex justify-between text-sm font-medium text-orange-800 dark:text-orange-200 pt-1 border-t">
            <span>Total devido:</span>
            <span>R$ 3.121,25</span>
          </div>
        </div>
      </div>
    </div>
  </MockupWrapper>
);

export const MockupCalcDosimetria = () => (
  <MockupWrapper label="Calculadora de dosimetria penal">
    <div className="p-4 space-y-4 max-w-md">
      <h3 className="font-semibold flex items-center gap-2">
        <Scale className="h-4 w-4 text-primary" />
        Dosimetria de Pena
      </h3>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-medium mb-1 block">Pena mínima (meses)</label>
            <input type="text" value="12" className="w-full px-3 py-2 border rounded-lg text-sm" />
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">Pena máxima (meses)</label>
            <input type="text" value="48" className="w-full px-3 py-2 border rounded-lg text-sm" />
          </div>
        </div>
        <div className="space-y-2">
          <p className="text-sm font-medium">Circunstâncias Judiciais (Art. 59):</p>
          <div className="space-y-2">
            {[
              { name: "Antecedentes", value: 0 },
              { name: "Conduta social", value: 1 },
              { name: "Personalidade", value: 0 },
              { name: "Motivos do crime", value: 2 },
            ].map((circ, i) => (
              <div key={i} className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{circ.name}</span>
                <div className="flex items-center gap-1">
                  <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-primary rounded-full" 
                      style={{ width: `${(circ.value / 5) * 100}%` }} 
                    />
                  </div>
                  <span className="text-xs w-4">{circ.value}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
        <button className="w-full py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium">
          Calcular Dosimetria
        </button>
        <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          <div className="flex justify-between text-sm font-medium text-red-800 dark:text-red-200">
            <span>Pena-base sugerida:</span>
            <span>18 meses (1 ano e 6 meses)</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">3 circunstâncias desfavoráveis de 8</p>
        </div>
      </div>
    </div>
  </MockupWrapper>
);

// === FASE 2: Novos Mockups ===

export const MockupTemplates = () => (
  <MockupWrapper label="Esta é a área de seleção de templates">
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold flex items-center gap-2">
          <LayoutTemplate className="h-4 w-4 text-primary" />
          Templates de Documentos
        </h3>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input type="text" placeholder="Buscar template..." className="pl-9 pr-3 py-1.5 border rounded-md text-sm w-48" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {[
          { name: "Petição Inicial Cível", icon: "📄", category: "Cível" },
          { name: "Contestação Trabalhista", icon: "⚖️", category: "Trabalhista" },
          { name: "Procuração Ad Judicia", icon: "📋", category: "Geral" },
          { name: "Contrato de Honorários", icon: "💼", category: "Contratos" },
          { name: "Recurso Ordinário", icon: "📑", category: "Recursos" },
          { name: "Habeas Corpus", icon: "🔓", category: "Penal" },
        ].map((template, i) => (
          <div key={i} className="p-3 border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer">
            <div className="flex items-start gap-3">
              <span className="text-2xl">{template.icon}</span>
              <div className="flex-1">
                <p className="text-sm font-medium">{template.name}</p>
                <span className="text-xs px-2 py-0.5 bg-muted rounded">{template.category}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-center text-muted-foreground">Clique em um template para começar</p>
    </div>
  </MockupWrapper>
);

export const MockupTema = () => (
  <MockupWrapper label="Este é o seletor de tema">
    <div className="p-4 space-y-4 max-w-sm">
      <h3 className="font-semibold flex items-center gap-2">
        <Palette className="h-4 w-4 text-primary" />
        Aparência
      </h3>
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">Escolha o tema da plataforma:</p>
        <div className="grid grid-cols-3 gap-2">
          <button className="p-3 border-2 border-primary rounded-lg text-center bg-white">
            <div className="w-10 h-10 mx-auto rounded-lg bg-gray-100 border mb-2 flex items-center justify-center">
              <Sun className="h-5 w-5 text-yellow-500" />
            </div>
            <span className="text-sm font-medium text-gray-900">Claro</span>
          </button>
          <button className="p-3 border rounded-lg text-center bg-gray-900">
            <div className="w-10 h-10 mx-auto rounded-lg bg-gray-800 border border-gray-700 mb-2 flex items-center justify-center">
              <Moon className="h-5 w-5 text-gray-300" />
            </div>
            <span className="text-sm font-medium text-white">Escuro</span>
          </button>
          <button className="p-3 border rounded-lg text-center hover:bg-muted/50">
            <div className="w-10 h-10 mx-auto rounded-lg bg-gradient-to-br from-gray-100 to-gray-800 border mb-2 flex items-center justify-center">
              <Monitor className="h-5 w-5 text-gray-500" />
            </div>
            <span className="text-sm font-medium">Sistema</span>
          </button>
        </div>
        <p className="text-xs text-muted-foreground text-center">O tema atual está destacado em azul</p>
      </div>
    </div>
  </MockupWrapper>
);

export const MockupGrupos = () => (
  <MockupWrapper label="Esta é a área de criação de grupos">
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold flex items-center gap-2">
          <Users className="h-4 w-4 text-primary" />
          Grupos de Trabalho
        </h3>
        <button className="flex items-center gap-2 px-3 py-1.5 bg-primary text-primary-foreground rounded-md text-sm">
          <Plus className="h-4 w-4" />
          Novo Grupo
        </button>
      </div>
      <div className="space-y-3">
        {[
          { name: "Equipe Trabalhista", members: 4, color: "bg-blue-100 text-blue-700" },
          { name: "Sócios", members: 2, color: "bg-purple-100 text-purple-700" },
          { name: "Estagiários", members: 6, color: "bg-green-100 text-green-700" },
        ].map((group, i) => (
          <div key={i} className="flex items-center gap-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors">
            <div className={cn("p-2 rounded-lg", group.color.split(" ")[0])}>
              <Users className={cn("h-4 w-4", group.color.split(" ")[1])} />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium">{group.name}</p>
              <p className="text-xs text-muted-foreground">{group.members} membros</p>
            </div>
            <div className="flex -space-x-2">
              {Array.from({ length: Math.min(group.members, 3) }).map((_, j) => (
                <div key={j} className="w-6 h-6 rounded-full bg-muted border-2 border-background flex items-center justify-center">
                  <User className="h-3 w-3 text-muted-foreground" />
                </div>
              ))}
              {group.members > 3 && (
                <div className="w-6 h-6 rounded-full bg-primary/10 border-2 border-background flex items-center justify-center">
                  <span className="text-[10px] font-medium text-primary">+{group.members - 3}</span>
                </div>
              )}
            </div>
            <button className="p-1.5 rounded hover:bg-muted">
              <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
            </button>
          </div>
        ))}
      </div>
      <div className="p-4 border-2 border-dashed border-muted-foreground/30 rounded-lg text-center">
        <Users className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
        <p className="text-sm text-muted-foreground">Crie grupos para compartilhar casos com várias pessoas de uma vez</p>
      </div>
    </div>
  </MockupWrapper>
);

export const MockupPermissoes = () => (
  <MockupWrapper label="Este é o gerenciamento de permissões">
    <div className="p-4 space-y-4 max-w-md">
      <h3 className="font-semibold flex items-center gap-2">
        <Lock className="h-4 w-4 text-primary" />
        Gerenciar Permissões
      </h3>
      <p className="text-sm text-muted-foreground">Controle o acesso ao caso "João Silva - Trabalhista"</p>
      <div className="space-y-3">
        {[
          { name: "Maria Santos", email: "maria@escritorio.com", permission: "Editar", color: "bg-blue-100 text-blue-700" },
          { name: "Pedro Costa", email: "pedro@escritorio.com", permission: "Visualizar", color: "bg-gray-100 text-gray-700" },
          { name: "Ana Lima", email: "ana@escritorio.com", permission: "Total", color: "bg-green-100 text-green-700" },
        ].map((person, i) => (
          <div key={i} className="flex items-center gap-3 p-3 border rounded-lg">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="h-4 w-4 text-primary" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium">{person.name}</p>
              <p className="text-xs text-muted-foreground">{person.email}</p>
            </div>
            <div className="flex items-center gap-2">
              <select className={cn("text-xs px-2 py-1 rounded border-0", person.color)}>
                <option>{person.permission}</option>
              </select>
              <button className="p-1 rounded hover:bg-muted">
                <Trash2 className="h-4 w-4 text-red-500" />
              </button>
            </div>
          </div>
        ))}
      </div>
      <div className="p-3 bg-muted/50 rounded-lg">
        <p className="text-xs text-muted-foreground">
          <strong>Visualizar:</strong> Ver o caso | <strong>Editar:</strong> Modificar documentos | <strong>Total:</strong> Tudo, inclusive compartilhar
        </p>
      </div>
    </div>
  </MockupWrapper>
);

export const MockupFirstSteps = () => (
  <MockupWrapper label="Este é o checklist de primeiros passos">
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold flex items-center gap-2">
          <Rocket className="h-4 w-4 text-primary" />
          Primeiros Passos
        </h3>
        <span className="text-sm font-medium text-primary">3 de 6 completos</span>
      </div>
      <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
        <div className="w-1/2 h-full bg-primary rounded-full" />
      </div>
      <div className="space-y-2">
        {[
          { title: "Faça login na plataforma", completed: true },
          { title: "Complete seu perfil", completed: true },
          { title: "Configure seu papel timbrado", completed: true },
          { title: "Crie seu primeiro caso", completed: false },
          { title: "Converse com a IA", completed: false },
          { title: "Gere seu primeiro documento", completed: false },
        ].map((step, i) => (
          <div 
            key={i} 
            className={cn(
              "flex items-center gap-3 p-3 rounded-lg border transition-colors",
              step.completed ? "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800" : "hover:bg-muted/50"
            )}
          >
            <div className={cn(
              "w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0",
              step.completed ? "bg-green-500" : "border-2 border-muted-foreground/30"
            )}>
              {step.completed && <Check className="h-4 w-4 text-white" />}
            </div>
            <span className={cn(
              "text-sm",
              step.completed ? "line-through text-muted-foreground" : "font-medium"
            )}>
              {step.title}
            </span>
            {!step.completed && (
              <button className="ml-auto text-xs px-3 py-1 bg-primary text-primary-foreground rounded">
                Fazer
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  </MockupWrapper>
);

export const MockupChatGeral = () => (
  <MockupWrapper label="Esta é a visão geral do Chat Jurídico">
    <div className="flex h-80">
      <div className="w-56 border-r p-3 space-y-3 bg-muted/30">
        <button className="w-full flex items-center gap-2 px-3 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium">
          <Plus className="h-4 w-4" />
          Nova Conversa
        </button>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input type="text" placeholder="Buscar..." className="w-full pl-9 pr-3 py-1.5 border rounded-md text-sm" />
        </div>
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground px-2 font-medium">Hoje</p>
          <button className="w-full text-left px-3 py-2 rounded-lg bg-muted text-sm truncate flex items-center gap-2">
            <MessageSquare className="h-3 w-3 flex-shrink-0" />
            Dúvida sobre FGTS
          </button>
          <button className="w-full text-left px-3 py-2 rounded-lg hover:bg-muted text-sm text-muted-foreground truncate flex items-center gap-2">
            <MessageSquare className="h-3 w-3 flex-shrink-0" />
            Prazo recursal
          </button>
        </div>
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground px-2 font-medium">Ontem</p>
          <button className="w-full text-left px-3 py-2 rounded-lg hover:bg-muted text-sm text-muted-foreground truncate flex items-center gap-2">
            <MessageSquare className="h-3 w-3 flex-shrink-0" />
            Súmula 331 TST
          </button>
          <button className="w-full text-left px-3 py-2 rounded-lg hover:bg-muted text-sm text-muted-foreground truncate flex items-center gap-2">
            <MessageSquare className="h-3 w-3 flex-shrink-0" />
            Cálculo de rescisão
          </button>
        </div>
      </div>
      <div className="flex-1 flex flex-col">
        <div className="border-b p-3 flex items-center justify-between">
          <h4 className="font-medium text-sm">Dúvida sobre FGTS</h4>
          <div className="flex items-center gap-1">
            <button className="p-1.5 rounded hover:bg-muted">
              <Download className="h-4 w-4 text-muted-foreground" />
            </button>
            <button className="p-1.5 rounded hover:bg-muted">
              <Share2 className="h-4 w-4 text-muted-foreground" />
            </button>
            <button className="p-1.5 rounded hover:bg-muted">
              <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
            </button>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center p-6">
            <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
            <p className="text-sm text-muted-foreground">Selecione uma conversa ou inicie uma nova</p>
          </div>
        </div>
        <div className="border-t p-3">
          <div className="flex items-center gap-2 px-3 py-2 border rounded-lg bg-muted/30">
            <input type="text" placeholder="Digite sua dúvida jurídica..." className="flex-1 text-sm bg-transparent outline-none" disabled />
            <Upload className="h-4 w-4 text-muted-foreground" />
            <Send className="h-4 w-4 text-muted-foreground" />
          </div>
        </div>
      </div>
    </div>
  </MockupWrapper>
);

// Mapping of mockup IDs to components
export const DOCS_MOCKUPS: Record<string, React.ReactNode> = {
  "casos-header": <MockupCasosHeader />,
  "caso-card": <MockupCasoCard />,
  "folder-card": <MockupFolderCard />,
  "base-conhecimento": <MockupBaseConhecimento />,
  "chat-caso": <MockupChatCaso />,
  "documentos-area": <MockupDocumentosArea />,
  "historico-docs": <MockupHistoricoDocs />,
  "compartilhar": <MockupCompartilhar />,
  "chat-juridico": <MockupChatJuridico />,
  "ferramentas-chat": <MockupFerramentasChat />,
  "assessor": <MockupAssessor />,
  "assessor-config": <MockupAssessorConfig />,
  "melhore-texto": <MockupMelhoreTexto />,
  "calculadoras": <MockupCalculadoras />,
  "configuracoes": <MockupConfiguracoes />,
  "papel-timbrado": <MockupPapelTimbrado />,
  "planos-creditos": <MockupPlanosCreditos />,
  "sidebar": <MockupSidebar />,
  // === FASE 1: Novos mockups ===
  "welcome": <MockupWelcome />,
  "interface-overview": <MockupInterfaceOverview />,
  "anexos": <MockupAnexos />,
  "historico-chat": <MockupHistoricoChat />,
  "escopo-assessor": <MockupEscopoAssessor />,
  "tom-resposta": <MockupTomResposta />,
  "tipos-melhoria": <MockupTiposMelhoria />,
  "calc-trabalhista": <MockupCalcTrabalhista />,
  "calc-inss": <MockupCalcINSS />,
  "calc-atualizacao": <MockupCalcAtualizacao />,
  "calc-pensao": <MockupCalcPensao />,
  "calc-aluguel": <MockupCalcAluguel />,
  "calc-dosimetria": <MockupCalcDosimetria />,
  // === FASE 2: Novos mockups ===
  "templates": <MockupTemplates />,
  "tema": <MockupTema />,
  "grupos": <MockupGrupos />,
  "permissoes": <MockupPermissoes />,
  "first-steps": <MockupFirstSteps />,
  "chat-geral": <MockupChatGeral />,
};
